import React, { useState, useEffect, useRef } from "react";
import { crewOpsApi } from "../../api";
import { toast } from "sonner";
import {
  Zap, X, Plus, RefreshCw, ChevronDown, AlertTriangle, CheckCircle, Clock, Plane,
  Map, Settings, BookmarkCheck, Trash2, BarChart2, Save, Users, Send,
} from "lucide-react";
import PairingRouteMap from "./PairingRouteMap";
import ScenarioRostering from "./ScenarioRostering";

// ─── IST-aware virtual today ─────────────────────────────────────────────────
const IST_NOW        = new Date(new Date().getTime() + 5.5 * 60 * 60 * 1000 - new Date().getTimezoneOffset() * 60000);
const CURRENT_YEAR   = IST_NOW.getFullYear();
const CURRENT_MONTH  = IST_NOW.getMonth() + 1;
const TODAY_DAY      = IST_NOW.getDate();
const DAYS_IN_MONTH  = new Date(CURRENT_YEAR, CURRENT_MONTH, 0).getDate();
const MONTH_NAME     = IST_NOW.toLocaleString("default", { month: "long" });
const MONTH_PREFIX   = `${CURRENT_YEAR}-${String(CURRENT_MONTH).padStart(2, "0")}`;
const MONTH_DAYS     = Array.from({ length: DAYS_IN_MONTH }, (_, i) => i + 1);
const DAY_W          = 34;

// ─── Base colour palette ──────────────────────────────────────────────────────
const BASE_COLORS = {
  DEL: "#f59e0b", BOM: "#38bdf8", BLR: "#34d399",
  HYD: "#a78bfa", MAA: "#fb923c", CCU: "#f87171",
};
const BASES = ["DEL", "BOM", "BLR", "HYD", "MAA", "CCU"];

// ─── Status badge ─────────────────────────────────────────────────────────────
function StatusDot({ status }) {
  const map = {
    active: "bg-emerald-500", aog: "bg-red-500", maintenance: "bg-amber-500 animate-pulse",
    completed: "bg-white/20", in_progress: "bg-teal-400 animate-pulse", scheduled: "bg-white/30",
    unassigned: "bg-white/20",
  };
  return <span data-testid={`status-dot-${status}`} className={`inline-block w-2 h-2 rounded-full flex-shrink-0 ${map[status] || "bg-white/20"}`} />;
}

// ─── Fleet Board ──────────────────────────────────────────────────────────────
function FleetBoard({ aircraft, selectedTail, onSelect, activeBase, onBase }) {
  return (
    <div className="w-56 flex-shrink-0 border-r border-border/40 flex flex-col overflow-hidden bg-card/20">
      <div className="px-3 py-3 border-b border-border/40">
        <div className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground mb-2">Fleet Board</div>
        <div className="flex flex-wrap gap-1">
          {["ALL", ...BASES].map(b => (
            <button
              key={b}
              data-testid={`base-filter-${b}`}
              onClick={() => onBase(b === "ALL" ? "" : b)}
              className="text-[9px] font-bold px-1.5 py-0.5 rounded-sm border transition-all"
              style={
                (activeBase === b) || (b === "ALL" && !activeBase)
                  ? { backgroundColor: `${BASE_COLORS[b] || "#ffffff"}20`, borderColor: BASE_COLORS[b] || "#ffffff", color: BASE_COLORS[b] || "#ffffff" }
                  : { backgroundColor: "transparent", borderColor: "rgba(255,255,255,0.1)", color: "rgba(255,255,255,0.4)" }
              }
            >{b}</button>
          ))}
        </div>
      </div>

      <div className="flex-1 overflow-y-auto">
        {BASES.filter(b => !activeBase || b === activeBase).map(base => {
          const tails = aircraft.filter(a => a.base === base);
          if (!tails.length) return null;
          const bc = BASE_COLORS[base];
          return (
            <div key={base}>
              <div className="flex items-center px-3 py-1.5 border-b border-border/20 sticky top-0 z-10"
                style={{ backgroundColor: `${bc}10` }}>
                <span className="text-[10px] font-black uppercase tracking-widest" style={{ color: bc }}>{base}</span>
                <span className="ml-auto text-[9px] text-muted-foreground">{tails.filter(t => t.status === "active").length}/{tails.length}</span>
              </div>
              {tails.map(ac => {
                const isSelected = selectedTail === ac.tail_number;
                const isGrounded = ac.status === "aog" || ac.status === "maintenance";
                return (
                  <button
                    key={ac.tail_number}
                    data-testid={`fleet-tail-${ac.tail_number}`}
                    onClick={() => onSelect(isSelected ? null : ac.tail_number)}
                    className={`w-full text-left px-3 py-2 border-b border-border/10 transition-all ${isSelected ? "bg-white/10" : "hover:bg-white/5"}`}
                  >
                    <div className="flex items-center gap-1.5">
                      <StatusDot status={ac.status} />
                      <span className={`font-mono text-[11px] font-bold ${isGrounded ? "text-red-400/70" : "text-foreground"}`}>
                        {ac.tail_number}
                      </span>
                    </div>
                    {!isGrounded && ac.aircraft_type && (
                      <div className="text-[9px] text-muted-foreground/60 font-mono mt-0.5 truncate">
                        {ac.aircraft_type.replace("Boeing ", "").replace("Airbus ", "")}
                      </div>
                    )}
                    {!isGrounded && (
                      <div className="mt-1 flex items-center gap-2">
                        <div className="flex-1 h-1 rounded-full bg-white/10 overflow-hidden">
                          <div
                            className="h-full rounded-full transition-all"
                            style={{ width: `${ac.utilization_pct || 0}%`, backgroundColor: ac.utilization_pct >= 85 ? "#34d399" : ac.utilization_pct >= 65 ? "#f59e0b" : "#f87171" }}
                          />
                        </div>
                        <span className="text-[9px] text-muted-foreground font-mono">{ac.utilization_pct || 0}%</span>
                      </div>
                    )}
                    {!isGrounded && ac.hours_per_day > 0 && (
                      <div className="text-[9px] text-muted-foreground mt-0.5">{ac.hours_per_day}h/day</div>
                    )}
                    {isGrounded && (
                      <div className="text-[9px] text-red-400 mt-0.5 font-bold uppercase">{ac.status}</div>
                    )}
                  </button>
                );
              })}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── Gantt Timeline ───────────────────────────────────────────────────────────
function GanttTimeline({ aircraft, pairings, selectedId, onSelect, view, optimResult, selectedGap, onSelectGap, filterTail }) {
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) {
      const todayLeft = (TODAY_DAY - 3) * DAY_W;
      scrollRef.current.scrollLeft = Math.max(0, todayLeft);
    }
  }, []);

  const byTail = {};
  pairings.forEach(p => {
    if (p.tail_number) (byTail[p.tail_number] = byTail[p.tail_number] || []).push(p);
  });

  const optimByTail = {};
  if (view === "Optimised" && optimResult?.optimised_pairings) {
    optimResult.optimised_pairings.forEach(op => {
      (optimByTail[op.tail_number] = optimByTail[op.tail_number] || []).push(op);
    });
  }

  const gapsByTail = {};
  if (view === "Gaps" && optimResult?.raw_gaps) {
    optimResult.raw_gaps.forEach(g => { gapsByTail[g.tail] = g.idle_windows; });
  }

  const displayAircraft = filterTail
    ? aircraft.filter(a => a.tail_number === filterTail)
    : aircraft;

  const totalW = DAYS_IN_MONTH * DAY_W;

  return (
    <div className="flex-1 min-w-0 flex flex-col overflow-hidden">
      {/* Date header */}
      <div className="flex border-b border-border/40 flex-shrink-0">
        <div className="w-28 flex-shrink-0 px-3 flex items-center border-r border-border/40">
          <span className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">{MONTH_NAME}</span>
        </div>
        <div className="flex-1 overflow-hidden" ref={scrollRef}>
          <div className="flex" style={{ width: totalW }}>
            {MONTH_DAYS.map(d => (
              <div
                key={d}
                className={`flex-shrink-0 flex items-center justify-center h-8 border-r border-border/20 text-[10px] font-mono select-none
                  ${d === TODAY_DAY ? "text-amber-400 font-black bg-amber-400/10" : d < TODAY_DAY ? "text-white/20" : "text-muted-foreground"}`}
                style={{ width: DAY_W }}
              >
                {d}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Rows */}
      <div className="flex-1 overflow-y-auto">
        {displayAircraft.map(ac => {
          const tailPairings = byTail[ac.tail_number] || [];
          const optimPairings = optimByTail[ac.tail_number] || [];
          const gaps = gapsByTail[ac.tail_number] || [];
          const bc = BASE_COLORS[ac.base] || "#f59e0b";
          const isGrounded = ac.status === "aog" || ac.status === "maintenance";

          return (
            <div key={ac.tail_number} className="flex border-b border-border/20 hover:bg-white/[0.02] transition-all" style={{ minHeight: 48 }}>
              <div className="w-28 flex-shrink-0 px-3 flex flex-col justify-center border-r border-border/20">
                <span className="font-mono text-[11px] font-bold truncate" style={{ color: bc }}>{ac.tail_number}</span>
                <span className="text-[9px] text-muted-foreground">{ac.base} · {isGrounded ? <span className="text-red-400 font-bold uppercase">{ac.status}</span> : `${ac.hours_per_day || 0}h/d`}</span>
              </div>

              <div className="flex-1 overflow-hidden">
                <div className="relative" style={{ width: totalW, height: 48 }}>
                  {MONTH_DAYS.map(d => (
                    <div key={d} className={`absolute top-0 bottom-0 border-r ${d === TODAY_DAY ? "border-amber-400/15" : "border-border/10"}`}
                      style={{ left: (d - 1) * DAY_W, width: DAY_W }} />
                  ))}
                  <div className="absolute inset-y-0 left-0 bg-black/25 pointer-events-none"
                    style={{ width: (TODAY_DAY - 1) * DAY_W }} />
                  <div className="absolute inset-y-0 w-0.5 bg-amber-400/50 z-10"
                    style={{ left: (TODAY_DAY - 1) * DAY_W + DAY_W / 2 - 1 }} />

                  {isGrounded && (
                    <div className="absolute inset-x-0 inset-y-1.5 rounded-sm flex items-center"
                      style={{
                        background: "repeating-linear-gradient(-45deg, rgba(239,68,68,0.08), rgba(239,68,68,0.08) 4px, transparent 4px, transparent 10px)",
                        border: "1px solid rgba(239,68,68,0.25)",
                      }}>
                      <span className="text-[9px] text-red-400/70 font-bold uppercase px-2 tracking-wider">{ac.status.toUpperCase()} — GROUNDED</span>
                    </div>
                  )}

                  {!isGrounded && tailPairings.filter(p => p.pairing_type === "flight").map(p => {
                    const sd = parseInt((p.start_date || "").split("-")[2] || "1");
                    const ed = parseInt((p.end_date || "").split("-")[2] || "1");
                    const left  = (sd - 1) * DAY_W + 2;
                    const width = Math.max((ed - sd + 1) * DAY_W - 4, 20);
                    const isPast   = ed < TODAY_DAY;
                    const isCurrent = sd <= TODAY_DAY && TODAY_DAY <= ed;
                    const opacity  = view === "Optimised" ? 0.35 : 1;
                    return (
                      <div
                        key={p.id}
                        data-testid={`pairing-block-${p.id}`}
                        onClick={() => onSelect(p)}
                        className="absolute top-2 bottom-2 rounded-sm cursor-pointer transition-all hover:brightness-125 overflow-hidden select-none"
                        style={{
                          left, width, opacity,
                          backgroundColor: isPast ? "rgba(245,158,11,0.07)" : isCurrent ? "rgba(245,158,11,0.28)" : "rgba(245,158,11,0.18)",
                          border: `1px solid ${isPast ? "rgba(245,158,11,0.2)" : isCurrent ? "rgba(245,158,11,0.7)" : "rgba(245,158,11,0.45)"}`,
                          boxShadow: selectedId === p.id ? "0 0 0 2px rgba(245,158,11,0.8)" : "none",
                        }}
                      >
                        <div className="px-1.5 leading-tight pt-0.5">
                          <div className="text-[9px] font-mono font-bold truncate text-amber-300/80">
                            {p.sectors?.[0] || p.pairing_code}
                          </div>
                          {ed > sd && (
                            <div className="text-[8px] text-amber-400/50 truncate">{p.total_fdp_hours}h</div>
                          )}
                        </div>
                      </div>
                    );
                  })}

                  {view === "Optimised" && optimPairings.map((op, i) => {
                    const left  = (op.start_day - 1) * DAY_W + 1;
                    const width = Math.max((op.end_day - op.start_day + 1) * DAY_W - 2, 20);
                    return (
                      <div key={`opt-${i}`}
                        className="absolute top-1 bottom-1 rounded-sm overflow-hidden border-2 flex flex-col justify-start"
                        style={{ left, width, backgroundColor: "rgba(45,212,191,0.18)", borderColor: "rgba(45,212,191,0.75)" }}>
                        <div className="px-1 pt-0.5 text-[9px] font-bold text-teal-300">AI</div>
                        <div className="px-1 text-[8px] text-teal-400/70 truncate">{op.sectors?.[0] || "+"}{op.fdp_hours}h</div>
                      </div>
                    );
                  })}

                  {view === "Gaps" && !isGrounded && gaps.map((gap, i) => {
                    const left  = (gap.start_day - 1) * DAY_W + 1;
                    const width = Math.max((gap.end_day - gap.start_day + 1) * DAY_W - 2, 12);
                    const isSel = selectedGap?.tail === ac.tail_number && selectedGap?.start_day === gap.start_day;
                    return (
                      <div key={`gap-${i}`}
                        data-testid={`gap-segment-${ac.tail_number}-${gap.start_day}`}
                        onClick={() => onSelectGap({ ...gap, tail: ac.tail_number, ac })}
                        className="absolute top-1.5 bottom-1.5 rounded-sm cursor-pointer overflow-hidden"
                        style={{
                          left, width,
                          backgroundColor: isSel ? "rgba(239,68,68,0.3)" : "rgba(239,68,68,0.14)",
                          border: `1px solid ${isSel ? "rgba(239,68,68,0.9)" : "rgba(239,68,68,0.4)"}`,
                          animation: "pulse 2s cubic-bezier(.4,0,.6,1) infinite",
                        }}>
                        <div className="px-1 pt-0.5 text-[8px] font-bold text-red-400 truncate">IDLE {gap.idle_days}d</div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── Detail Panel ─────────────────────────────────────────────────────────────
function DetailPanel({ pairing, onClose }) {
  if (!pairing) return null;
  const sd = parseInt((pairing.start_date || "").split("-")[2] || "1");
  const ed = parseInt((pairing.end_date || "").split("-")[2] || "1");
  const STATUS_COLORS = { completed: "text-white/40", in_progress: "text-teal-400", scheduled: "text-blue-400", unassigned: "text-white/40" };
  return (
    <div className="w-72 flex-shrink-0 border-l border-border/40 flex flex-col overflow-hidden bg-card/10">
      <div className="px-4 py-3 border-b border-border/40 flex items-center justify-between">
        <div>
          <div className="font-mono text-sm font-bold text-foreground">{pairing.pairing_code}</div>
          <div className={`text-[10px] font-bold uppercase ${STATUS_COLORS[pairing.status] || "text-muted-foreground"}`}>{pairing.status}</div>
        </div>
        <button data-testid="detail-panel-close" onClick={onClose} className="text-muted-foreground hover:text-foreground transition-colors"><X size={14} /></button>
      </div>
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        <div className="grid grid-cols-2 gap-2">
          {[["Tail", pairing.tail_number], ["Fleet", "B737 MAX 8"], ["Days", `${sd}–${ed}`], ["FDP", `${pairing.total_fdp_hours}h`]].map(([label, val]) => (
            <div key={label} className="bg-white/5 rounded-sm p-2">
              <div className="text-[9px] text-muted-foreground uppercase">{label}</div>
              <div className="text-xs font-mono font-bold text-foreground mt-0.5">{val}</div>
            </div>
          ))}
        </div>
        <div>
          <div className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground mb-2">Sectors</div>
          <div className="space-y-1">
            {(pairing.sector_detail || pairing.sectors || []).map((s, i) => {
              const dep = typeof s === "object" ? s.dep : s?.split("-")[0];
              const arr = typeof s === "object" ? s.arr : s?.split("-")[1];
              const flight = typeof s === "object" ? s.flight : "";
              const block  = typeof s === "object" ? s.block_hours : "";
              return (
                <div key={i} className="flex items-center gap-2 px-2 py-1.5 bg-white/5 rounded-sm">
                  <span className="font-mono text-[10px] font-bold text-amber-300">{dep}</span>
                  <Plane size={8} className="text-white/30 rotate-90" />
                  <span className="font-mono text-[10px] font-bold text-amber-300">{arr}</span>
                  {flight && <span className="ml-auto text-[9px] text-muted-foreground">{flight}</span>}
                  {block  && <span className="text-[9px] text-white/40 font-mono">{block}h</span>}
                </div>
              );
            })}
          </div>
        </div>
        {pairing.pairing_notes && (
          <div className="text-[10px] text-muted-foreground border-t border-border/30 pt-3">{pairing.pairing_notes}</div>
        )}
      </div>
    </div>
  );
}

// ─── Gap Detail Panel ─────────────────────────────────────────────────────────
function GapDetailPanel({ gap, optimResult, onClose }) {
  if (!gap) return null;
  const rec = optimResult?.recommendations?.find(r => r.affected_tail === gap.tail);
  return (
    <div className="w-72 flex-shrink-0 border-l border-border/40 flex flex-col overflow-hidden bg-card/10">
      <div className="px-4 py-3 border-b border-border/40 flex items-center justify-between">
        <div>
          <div className="font-mono text-sm font-bold text-foreground">{gap.tail}</div>
          <div className="text-[10px] font-bold uppercase text-red-400">{gap.idle_days} Day Idle Window</div>
        </div>
        <button data-testid="gap-panel-close" onClick={onClose} className="text-muted-foreground hover:text-foreground transition-colors"><X size={14} /></button>
      </div>
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        <div className="bg-red-500/10 border border-red-500/25 rounded-sm p-3">
          <div className="text-[9px] font-bold uppercase tracking-widest text-red-400 mb-1">Idle Period</div>
          <div className="text-sm font-mono font-bold text-foreground">Day {gap.start_day} — Day {gap.end_day}</div>
          <div className="text-xs text-muted-foreground mt-0.5">{gap.idle_days} days without assignment · {gap.ac?.base} base</div>
        </div>
        {rec ? (
          <div>
            <div className="text-[10px] font-bold uppercase tracking-widest text-teal-400 mb-2">AI Recommendation</div>
            <div className="bg-teal-500/10 border border-teal-500/25 rounded-sm p-3 space-y-2.5">
              <div className="flex items-start gap-2">
                <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded-sm flex-shrink-0 mt-0.5 ${rec.priority === "high" ? "text-red-400 bg-red-500/15" : rec.priority === "medium" ? "text-amber-400 bg-amber-500/15" : "text-emerald-400 bg-emerald-500/15"}`}>
                  {(rec.priority || "").toUpperCase()}
                </span>
                <div className="text-xs font-bold text-foreground">{rec.title}</div>
              </div>
              <div className="text-[10px] text-muted-foreground">{rec.current_gap}</div>
              <div className="flex flex-wrap gap-1">
                {(rec.suggested_sectors || []).map(s => (
                  <span key={s} className="text-[10px] font-mono px-1.5 py-0.5 bg-teal-500/20 border border-teal-500/30 rounded-sm text-teal-300">{s}</span>
                ))}
              </div>
              <div className="grid grid-cols-2 gap-2">
                {[["Gain", `+${rec.gain_hours}h`, "text-teal-400"], ["FDP", `${rec.fdp_total}h`, "text-foreground"]].map(([l, v, c]) => (
                  <div key={l} className="bg-white/5 rounded-sm p-2">
                    <div className="text-[9px] text-muted-foreground">{l}</div>
                    <div className={`text-xs font-mono font-bold ${c} mt-0.5`}>{v}</div>
                  </div>
                ))}
              </div>
              <div className="text-[9px] text-amber-400">{rec.compliance_note}</div>
            </div>
          </div>
        ) : (
          <div className="bg-white/5 border border-border/30 rounded-sm p-4 text-center">
            <Zap size={16} className="text-teal-400/50 mx-auto mb-2" />
            <div className="text-xs text-muted-foreground">Run <span className="text-teal-400 font-bold">Optimise Fleet (AI)</span> to get recommendations for this gap.</div>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── AI Insights Panel ────────────────────────────────────────────────────────
function AIInsightsPanel({ result, setView, onClose }) {
  return (
    <div className="w-80 flex-shrink-0 border-l border-border/40 flex flex-col overflow-hidden bg-card/10">
      <div className="px-4 py-3 border-b border-border/40 flex items-center gap-2">
        <Zap size={13} className="text-teal-400" />
        <div className="text-sm font-bold text-foreground">Fleet Optimisation</div>
        <div className="ml-auto flex items-baseline gap-0.5">
          <span className="text-lg font-mono font-black" style={{ color: result.efficiency_score >= 70 ? "#22c55e" : result.efficiency_score >= 50 ? "#f59e0b" : "#ef4444" }}>
            {result.efficiency_score}
          </span>
          <span className="text-[9px] text-muted-foreground">/100</span>
        </div>
        <button onClick={onClose} className="ml-2 text-muted-foreground hover:text-foreground transition-colors"><X size={14} /></button>
      </div>

      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        <div className="bg-amber-500/10 border border-amber-500/25 rounded-sm p-3">
          <div className="flex items-center gap-1.5 mb-1.5">
            <AlertTriangle size={10} className="text-amber-400" />
            <span className="text-[9px] font-bold uppercase tracking-widest text-amber-400">Priority Action</span>
          </div>
          <div className="text-xs text-foreground leading-relaxed">{result.priority_action}</div>
        </div>

        <div>
          <div className="text-[9px] font-bold uppercase tracking-widest text-muted-foreground mb-1.5">Assessment</div>
          <div className="text-[11px] text-muted-foreground leading-relaxed">{result.narrative}</div>
        </div>

        {result.gap_summary && (
          <div className="grid grid-cols-3 gap-2">
            {[
              ["Idle Days", result.gap_summary.total_idle_tail_days, "text-red-400"],
              ["Recoverable", `${result.gap_summary.recoverable_hours}h`, "text-teal-400"],
              ["Focus", result.gap_summary.highest_priority_base, "text-foreground"],
            ].map(([l, v, c]) => (
              <div key={l} className="bg-white/5 rounded-sm p-2 text-center">
                <div className="text-[8px] text-muted-foreground uppercase">{l}</div>
                <div className={`text-sm font-mono font-bold ${c} mt-0.5`}>{v}</div>
              </div>
            ))}
          </div>
        )}

        <div>
          <div className="text-[9px] font-bold uppercase tracking-widest text-muted-foreground mb-2">Recommendations</div>
          <div className="space-y-2">
            {(result.recommendations || []).map((rec, i) => (
              <div key={rec.id || i} className="bg-white/5 border border-border/30 rounded-sm p-3">
                <div className="flex items-start justify-between gap-2 mb-1.5">
                  <div className="text-xs font-bold text-foreground leading-tight">{rec.title}</div>
                  <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded-sm flex-shrink-0 ${rec.priority === "high" ? "text-red-400 bg-red-500/15" : rec.priority === "medium" ? "text-amber-400 bg-amber-500/15" : "text-emerald-400 bg-emerald-500/15"}`}>
                    {(rec.priority || "").toUpperCase()}
                  </span>
                </div>
                <div className="font-mono text-[10px] text-teal-400 mb-1">{rec.affected_tail} · {rec.base}</div>
                <div className="text-[10px] text-muted-foreground mb-1.5">{rec.current_gap}</div>
                <div className="flex flex-wrap gap-1 mb-2">
                  {(rec.suggested_sectors || []).map(s => (
                    <span key={s} className="text-[9px] font-mono px-1 py-0.5 bg-teal-500/15 border border-teal-500/25 rounded-sm text-teal-300">{s}</span>
                  ))}
                </div>
                <div className="flex items-center gap-3 text-[10px]">
                  <span className="text-teal-400 font-bold">+{rec.gain_hours}h gain</span>
                  <span className="text-muted-foreground">{rec.fdp_total}h FDP</span>
                </div>
                <div className="text-[9px] text-amber-400 mt-1">{rec.compliance_note}</div>
              </div>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-2 gap-2 pt-2">
          <button onClick={() => setView("Optimised")}
            className="py-2 text-xs font-bold rounded-sm bg-teal-500/15 border border-teal-500/35 text-teal-300 hover:bg-teal-500/25 transition-all">
            View Optimised
          </button>
          <button onClick={() => setView("Gaps")}
            className="py-2 text-xs font-bold rounded-sm bg-red-500/15 border border-red-500/35 text-red-300 hover:bg-red-500/25 transition-all">
            View Gaps
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Create Pairing Modal ─────────────────────────────────────────────────────
function CreatePairingModal({ aircraft, onClose, onCreate }) {
  const [form, setForm] = useState({ tail_number: "", start_date: `${MONTH_PREFIX}-${String(TODAY_DAY).padStart(2,"0")}`, end_date: `${MONTH_PREFIX}-${String(Math.min(TODAY_DAY + 1, DAYS_IN_MONTH)).padStart(2,"0")}`, sectors: "DEL-BOM,BOM-DEL" });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async () => {
    setSaving(true);
    try {
      await onCreate({ ...form, sectors: form.sectors.split(",").map(s => s.trim()).filter(Boolean), source: "akasa_schedule" });
      onClose();
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-card border border-border rounded-sm w-full max-w-md">
        <div className="px-4 py-3 border-b border-border flex items-center justify-between">
          <div className="font-bold text-sm text-foreground">New Rotation Block</div>
          <button onClick={onClose}><X size={14} className="text-muted-foreground" /></button>
        </div>
        <div className="p-4 space-y-3">
          {[["Tail Number", "tail_number", "select"], ["Start Date", "start_date", "date"], ["End Date", "end_date", "date"], ["Sectors (comma-separated)", "sectors", "text"]].map(([label, key, type]) => (
            <div key={key}>
              <label className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground block mb-1">{label}</label>
              {type === "select" ? (
                <select value={form[key]} onChange={e => setForm(p => ({ ...p, [key]: e.target.value }))}
                  className="w-full bg-white/5 border border-border rounded-sm px-3 py-2 text-sm text-foreground">
                  <option value="">Select tail...</option>
                  {aircraft.filter(a => a.status === "active").map(a => (
                    <option key={a.tail_number} value={a.tail_number}>{a.tail_number} ({a.base})</option>
                  ))}
                </select>
              ) : (
                <input type={type} value={form[key]} onChange={e => setForm(p => ({ ...p, [key]: e.target.value }))}
                  className="w-full bg-white/5 border border-border rounded-sm px-3 py-2 text-sm text-foreground font-mono" />
              )}
            </div>
          ))}
        </div>
        <div className="px-4 py-3 border-t border-border flex gap-2 justify-end">
          <button onClick={onClose} className="px-3 py-1.5 text-xs text-muted-foreground border border-border rounded-sm hover:bg-white/5">Cancel</button>
          <button data-testid="create-pairing-submit" onClick={handleSubmit} disabled={saving || !form.tail_number}
            className="px-4 py-1.5 text-xs font-bold rounded-sm bg-amber-500/20 border border-amber-500/50 text-amber-300 hover:bg-amber-500/30 disabled:opacity-40 transition-all">
            {saving ? "Creating..." : "Create"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Fleet Settings Tab ───────────────────────────────────────────────────────
function FleetSettingsTab({ settings, onSave }) {
  const defaultFleets = [
    { fleet_type: "B737 MAX 8",  target_bh_day: 12.0, min_ground_min: 45, max_sectors_day: 6 },
    { fleet_type: "A320neo",     target_bh_day: 11.5, min_ground_min: 40, max_sectors_day: 6 },
    { fleet_type: "B777-300ER",  target_bh_day: 16.0, min_ground_min: 90, max_sectors_day: 4 },
    { fleet_type: "ATR 72-600",  target_bh_day: 9.0,  min_ground_min: 30, max_sectors_day: 8 },
  ];

  const [rows, setRows] = useState(settings?.fleet_types || defaultFleets);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (settings?.fleet_types?.length) setRows(settings.fleet_types);
  }, [settings]);

  const updateRow = (idx, field, value) => {
    setRows(prev => prev.map((r, i) => i === idx ? { ...r, [field]: value } : r));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave({ fleet_types: rows });
      toast.success("Fleet settings saved");
    } catch { toast.error("Save failed"); }
    finally { setSaving(false); }
  };

  const FIELD_INFO = {
    target_bh_day:   { label: "Target BH/day",      unit: "h",   min: 1,  max: 24,  step: 0.5, tip: "Target block hours per aircraft per day for utilisation calculation." },
    min_ground_min:  { label: "Min Ground Time",     unit: "min", min: 15, max: 240, step: 5,   tip: "Minimum turnaround time at station between flights." },
    max_sectors_day: { label: "Max Sectors/day",     unit: "",    min: 1,  max: 12,  step: 1,   tip: "Maximum permitted flight sectors per aircraft per day." },
  };

  return (
    <div className="flex-1 overflow-y-auto p-6" data-testid="fleet-settings-tab">
      <div className="max-w-3xl mx-auto space-y-6">
        <div>
          <h3 className="text-sm font-black uppercase tracking-widest text-foreground mb-1">Fleet Settings</h3>
          <p className="text-[11px] text-muted-foreground leading-relaxed">
            Configure operational parameters for each fleet type. The AI optimisation engine uses these values to compute utilisation gaps and recommend pairing adjustments.
          </p>
        </div>

        <div className="border border-border/60 rounded-sm overflow-hidden">
          {/* Header */}
          <div className="grid grid-cols-4 border-b border-border bg-white/5">
            <div className="px-4 py-3 text-[10px] font-bold uppercase tracking-widest text-muted-foreground">Fleet Type</div>
            {Object.values(FIELD_INFO).map(fi => (
              <div key={fi.label} className="px-4 py-3 text-center text-[10px] font-bold uppercase tracking-widest text-muted-foreground">
                {fi.label}
              </div>
            ))}
          </div>

          {/* Rows */}
          {rows.map((row, i) => {
            const isActive = row.fleet_type.includes("737");
            return (
              <div
                key={i}
                className={`grid grid-cols-4 border-b border-border/30 transition-all ${isActive ? "bg-amber-500/5" : "hover:bg-white/[0.02]"}`}
                data-testid={`fleet-row-${i}`}
              >
                <div className="px-4 py-4 flex items-center gap-2">
                  <span className="font-mono text-xs font-bold text-foreground">{row.fleet_type}</span>
                  {isActive && (
                    <span className="text-[8px] font-bold px-1.5 py-0.5 rounded-sm bg-amber-500/20 border border-amber-500/40 text-amber-400 uppercase">Active</span>
                  )}
                </div>

                {Object.entries(FIELD_INFO).map(([field, fi]) => (
                  <div key={field} className="px-4 py-4 flex items-center justify-center">
                    <div className="flex items-center gap-1.5">
                      <input
                        type="number"
                        step={fi.step}
                        min={fi.min}
                        max={fi.max}
                        value={row[field] ?? ""}
                        onChange={e => {
                          const v = fi.step < 1 ? parseFloat(e.target.value) : parseInt(e.target.value);
                          updateRow(i, field, isNaN(v) ? 0 : v);
                        }}
                        className="w-16 bg-white/5 border border-border/60 rounded-sm px-2 py-1.5 text-xs text-foreground font-mono text-center focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary/50 transition-all"
                        data-testid={`setting-${field}-${i}`}
                      />
                      <span className="text-[9px] text-muted-foreground">{fi.unit}</span>
                    </div>
                  </div>
                ))}
              </div>
            );
          })}
        </div>

        {/* Tips */}
        <div className="grid grid-cols-3 gap-3">
          {Object.values(FIELD_INFO).map(fi => (
            <div key={fi.label} className="p-3 bg-white/[0.03] border border-border/30 rounded-sm">
              <div className="text-[9px] font-bold uppercase tracking-widest text-muted-foreground mb-1">{fi.label}</div>
              <div className="text-[10px] text-muted-foreground/70 leading-relaxed">{fi.tip}</div>
            </div>
          ))}
        </div>

        {/* Save bar */}
        <div className="flex items-center justify-between p-4 bg-amber-500/5 border border-amber-500/20 rounded-sm">
          <div>
            <div className="text-xs font-bold text-amber-300">B737 MAX 8 is the active fleet type</div>
            <div className="text-[10px] text-muted-foreground">Changes take effect on the next AI optimisation run.</div>
          </div>
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex items-center gap-1.5 px-4 py-2 text-xs font-bold rounded-sm bg-primary/20 border border-primary/40 text-primary hover:bg-primary/30 disabled:opacity-40 transition-all"
            data-testid="save-fleet-settings-btn"
          >
            {saving ? <RefreshCw size={11} className="animate-spin" /> : <Save size={11} />}
            {saving ? "Saving..." : "Save Settings"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Scenarios Tab ────────────────────────────────────────────────────────────
function ScenariosTab({ activeBase, onLoad, onSubmit }) {
  const [scenarios, setScenarios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterBase, setFilterBase] = useState(activeBase || "");
  const [deleting, setDeleting] = useState(null);
  const [submitting, setSubmitting] = useState(null);

  const BASE_LIST = ["", "DEL", "BOM", "BLR", "HYD", "MAA", "CCU", "AMD", "GOA"];

  const loadScenarios = async () => {
    setLoading(true);
    try {
      const r = await crewOpsApi.listPairingScenarios(filterBase || undefined);
      setScenarios(r.data || []);
    } catch { toast.error("Failed to load scenarios"); }
    finally { setLoading(false); }
  };

  useEffect(() => { loadScenarios(); }, [filterBase]);

  const handleDelete = async (id) => {
    setDeleting(id);
    try {
      await crewOpsApi.deletePairingScenario(id);
      toast.success("Scenario deleted");
      setScenarios(prev => prev.filter(s => s.id !== id));
    } catch { toast.error("Delete failed"); }
    finally { setDeleting(null); }
  };

  const handleSubmit = async (s) => {
    if (!s.schedule?.tail_number) {
      toast.error("Scenario has no schedule — cannot submit to rostering");
      return;
    }
    setSubmitting(s.id);
    try {
      await crewOpsApi.submitScenarioToRostering(s.id);
      toast.success(`"${s.name}" submitted to Crew Rostering`);
      if (onSubmit) onSubmit();
    } catch (e) { toast.error(e?.response?.data?.detail || "Submit failed"); }
    finally { setSubmitting(null); }
  };

  return (
    <div className="flex-1 overflow-y-auto p-6" data-testid="scenarios-tab">
      <div className="max-w-4xl mx-auto space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-sm font-black uppercase tracking-widest text-foreground mb-1">Saved Scenarios</h3>
            <p className="text-[11px] text-muted-foreground">Load any saved scenario onto the Route Map to compare pairing strategies by base.</p>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-[10px] text-muted-foreground uppercase tracking-widest">Base</span>
            <select
              value={filterBase}
              onChange={e => setFilterBase(e.target.value)}
              className="bg-card border border-border rounded-sm px-2 py-1 text-xs text-foreground focus:outline-none"
              data-testid="scenarios-base-filter"
            >
              {BASE_LIST.map(b => <option key={b} value={b}>{b || "All Bases"}</option>)}
            </select>
            <button onClick={loadScenarios} className="p-1.5 border border-border/40 rounded-sm text-muted-foreground hover:text-foreground transition-colors" data-testid="refresh-scenarios-btn">
              <RefreshCw size={12} />
            </button>
          </div>
        </div>

        {loading ? (
          <div className="text-center py-12 text-muted-foreground text-sm animate-pulse">Loading scenarios...</div>
        ) : scenarios.length === 0 ? (
          <div className="text-center py-16 space-y-3 border border-border/20 rounded-sm bg-white/[0.02]">
            <BookmarkCheck size={32} className="text-muted-foreground/20 mx-auto" />
            <div className="text-sm text-muted-foreground">No saved scenarios yet</div>
            <div className="text-[11px] text-muted-foreground/50 max-w-xs mx-auto">
              Go to the Route Map tab, run the AI optimiser, then click "Save Scenario" to store a snapshot here.
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            {scenarios.map(s => {
              const util = s.utilization_summary;
              const optScore = s.optimisation_result?.efficiency_score;
              const pairingCount = (s.pairings || []).length;
              const created = new Date(s.created_at).toLocaleDateString("en-IN", {
                day: "2-digit", month: "short", year: "numeric",
              });

              return (
                <div
                  key={s.id}
                  className="border border-border/50 rounded-sm bg-card/30 p-4 hover:border-border/80 hover:bg-card/50 transition-all group"
                  data-testid={`scenario-card-${s.id}`}
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1.5">
                        <span className="font-bold text-sm text-foreground truncate">{s.name}</span>
                        <span
                          className="flex-shrink-0 text-[9px] font-bold px-1.5 py-0.5 rounded-sm border"
                          style={{ backgroundColor: `${BASE_COLORS[s.base] || "#6366f1"}15`, borderColor: `${BASE_COLORS[s.base] || "#6366f1"}40`, color: BASE_COLORS[s.base] || "#6366f1" }}
                        >
                          {s.base}
                        </span>
                      </div>

                      {s.description && (
                        <div className="text-[10px] text-muted-foreground mb-2 leading-relaxed">{s.description}</div>
                      )}

                      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-[10px] text-muted-foreground">
                        <span className="flex items-center gap-1">
                          <Plane size={9} className="text-amber-400/70" />
                          {pairingCount} pairings
                        </span>
                        {util && (
                          <>
                            <span className="flex items-center gap-1">
                              <BarChart2 size={9} />
                              Fleet util:
                              <span className={`font-bold ml-0.5 ${util.fleet_avg_util_pct >= 75 ? "text-green-400" : util.fleet_avg_util_pct >= 55 ? "text-amber-400" : "text-red-400"}`}>
                                {util.fleet_avg_util_pct}%
                              </span>
                            </span>
                            <span className="text-red-400/70">{util.total_lost_bh}h lost BH</span>
                          </>
                        )}
                        {optScore != null && (
                          <span className="flex items-center gap-1">
                            <Zap size={9} className="text-teal-400" />
                            <span className="text-teal-400 font-bold">AI Score: {optScore}/100</span>
                          </span>
                        )}
                        <span className="ml-auto opacity-60">{created}</span>
                      </div>
                    </div>

                    <div className="flex items-center gap-2 flex-shrink-0">
                      <button
                        onClick={() => onLoad(s)}
                        className="flex items-center gap-1.5 px-3 py-1.5 text-[10px] font-bold rounded-sm bg-teal-500/10 border border-teal-500/30 text-teal-300 hover:bg-teal-500/20 transition-all"
                        data-testid={`load-scenario-btn-${s.id}`}
                      >
                        <Map size={10} /> Load on Map
                      </button>
                      <button
                        onClick={() => handleSubmit(s)}
                        disabled={submitting === s.id}
                        className="flex items-center gap-1.5 px-3 py-1.5 text-[10px] font-bold rounded-sm bg-indigo-500/10 border border-indigo-500/30 text-indigo-300 hover:bg-indigo-500/20 disabled:opacity-40 transition-all"
                        data-testid={`submit-rostering-btn-${s.id}`}
                        title="Submit to Crew Rostering"
                      >
                        {submitting === s.id ? <RefreshCw size={10} className="animate-spin" /> : <Send size={10} />}
                        {submitting === s.id ? "Submitting…" : "Submit to Rostering"}
                      </button>
                      <button
                        onClick={() => handleDelete(s.id)}
                        disabled={deleting === s.id}
                        className="p-1.5 text-muted-foreground hover:text-red-400 border border-border/30 hover:border-red-400/30 rounded-sm transition-all disabled:opacity-40"
                        data-testid={`delete-scenario-btn-${s.id}`}
                        title="Delete scenario"
                      >
                        {deleting === s.id ? <RefreshCw size={10} className="animate-spin" /> : <Trash2 size={10} />}
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────
export default function PairingBuilder() {
  const [aircraft,         setAircraft]        = useState([]);
  const [pairings,         setPairings]         = useState([]);
  const [loading,          setLoading]          = useState(true);
  const [selectedId,       setSelectedId]       = useState(null);
  const [selectedGap,      setSelectedGap]      = useState(null);
  const [activeBase,       setActiveBase]       = useState("");
  const [filterTail,       setFilterTail]       = useState("");
  const [ganttView,        setGanttView]        = useState("Present");
  const [optimResult,      setOptimResult]      = useState(null);
  const [optimLoading,     setOptimLoading]     = useState(false);
  const [showCreate,       setShowCreate]       = useState(false);

  // Main tabs
  const [activeTab, setActiveTab] = useState("gantt"); // "gantt" | "route-map" | "fleet-settings" | "scenarios"

  // Fleet Settings
  const [fleetSettings, setFleetSettings] = useState(null);

  // Scenario overlay state (used in Route Map tab)
  const [mapPairings,        setMapPairings]        = useState(null); // null = live pairings
  const [mapOptimResult,     setMapOptimResult]     = useState(null); // null = live optimResult
  const [mapScenario,        setMapScenario]        = useState(null); // full schedule to restore
  const [loadedScenarioName, setLoadedScenarioName] = useState(null);

  const selectedPairing = pairings.find(p => p.id === selectedId);

  useEffect(() => {
    loadData();
    // Load cached optimisation result
    crewOpsApi.getOptimizationResult().then(r => { if (r?.data?.efficiency_score) setOptimResult(r.data); }).catch(() => {});
    // Load fleet settings
    crewOpsApi.getPairingSettings().then(r => setFleetSettings(r.data)).catch(() => {});
  }, []);

  async function loadData() {
    setLoading(true);
    try {
      const [fRes, pRes] = await Promise.all([
        crewOpsApi.getFleet(),
        crewOpsApi.getPairings({ source: "akasa_schedule" }),
      ]);
      setAircraft(fRes.data || []);
      setPairings(pRes.data || []);
    } catch (err) { console.error("Failed to load pairing data:", err); }
    finally { setLoading(false); }
  }

  async function handleOptimise(force = false) {
    setOptimLoading(true);
    try {
      const res = await crewOpsApi.optimizeFleet(force);
      setOptimResult(res.data);
      setGanttView("Optimised");
      setSelectedGap(null);
      setSelectedId(null);
    } catch (err) {
      console.error("Optimisation failed:", err);
    } finally { setOptimLoading(false); }
  }

  async function handleCreate(data) {
    await crewOpsApi.createPairing(data);
    await loadData();
  }

  async function handleSaveSettings(data) {
    await crewOpsApi.updatePairingSettings(data);
    setFleetSettings(data);
  }

  function handleLoadScenario(scenario) {
    setMapPairings(scenario.pairings || []);
    setMapOptimResult(scenario.optimisation_result || null);
    setMapScenario(scenario);
    setLoadedScenarioName(scenario.name);
    setActiveTab("route-map");
    toast.success(`Loaded "${scenario.name}" on Route Map`);
  }

  function handleClearScenario() {
    setMapPairings(null);
    setMapOptimResult(null);
    setMapScenario(null);
    setLoadedScenarioName(null);
  }

  // Stats — update based on current view / optimised state
  const activeCount    = aircraft.filter(a => a.status === "active").length;
  const groundedCount  = aircraft.filter(a => a.status === "aog" || a.status === "maintenance").length;

  // Effective pairings: use scenario pairings when loaded on Route Map, otherwise base pairings
  const effectivePairings = mapPairings !== null ? mapPairings : pairings;
  const flightPairings    = effectivePairings.filter(p => p.pairing_type === "flight");

  // Extra rotations from AI-optimised plan (when in Optimised view)
  const optimisedExtras   = (ganttView === "Optimised" && optimResult?.optimised_pairings?.length)
    ? optimResult.optimised_pairings.length : 0;
  // Extra legs from loaded scenario (Route Map)
  const scenarioExtraLegs = (mapScenario?.schedule?.extra_legs?.length || 0);
  const displayRotations  = flightPairings.length + optimisedExtras + Math.floor(scenarioExtraLegs / 2);

  // Avg utilisation — when Optimised view use efficiency_score from AI result
  const baseUtil = aircraft.length > 0
    ? Math.round(aircraft.filter(a => a.status === "active").reduce((s, a) => s + (a.utilization_pct || 0), 0) / (activeCount || 1))
    : 0;
  const avgUtil = (ganttView === "Optimised" && optimResult?.efficiency_score)
    ? optimResult.efficiency_score
    : baseUtil;

  // Avg block hours — when Optimised view, add gain_hours from recommendations spread over active fleet
  const avgHpd = (() => {
    const active = aircraft.filter(a => a.status === "active" && a.hours_per_day > 0);
    if (active.length === 0) return "—";
    const baseHours  = active.reduce((s, a) => s + a.hours_per_day, 0);
    const gainHours  = (ganttView === "Optimised" && optimResult?.recommendations)
      ? optimResult.recommendations.reduce((s, r) => s + (r.gain_hours || 0), 0) : 0;
    const extraScenH = mapScenario?.schedule?.total_extra_bh || 0;
    return ((baseHours + gainHours + extraScenH) / active.length).toFixed(1);
  })();

  const displayAircraft = activeBase ? aircraft.filter(a => a.base === activeBase) : aircraft;

  const GANTT_VIEWS = ["Present", "Optimised", "Gaps"];

  const showAIPanel    = !selectedPairing && !selectedGap && optimResult && ganttView !== "Present";
  const showGapPanel   = !!selectedGap;
  const showDetailPanel = !!selectedPairing && !selectedGap;

  const MAIN_TABS = [
    { id: "gantt",          icon: BarChart2,     label: "Gantt" },
    { id: "route-map",      icon: Map,           label: "Route Map" },
    { id: "fleet-settings", icon: Settings,      label: "Fleet Settings" },
    { id: "scenarios",      icon: BookmarkCheck, label: "Scenarios" },
    { id: "rostering",      icon: Users,         label: "Crew Rostering" },
  ];

  return (
    <div data-testid="pairing-builder" className="flex flex-col h-full bg-background text-foreground overflow-hidden">
      {/* ── Header ─────────────────────────────────────────────────────────── */}
      <div className="flex items-center gap-3 px-4 py-3 border-b border-border/40 flex-shrink-0">
        <div>
          <h2 className="text-sm font-black uppercase tracking-widest text-foreground">Pairing Builder</h2>
          <div className="text-[10px] text-muted-foreground">
            B737 MAX 8 · <span className="text-amber-400 font-bold">{MONTH_NAME}</span>
            {" "}· Day <span className="font-mono font-bold text-foreground">{TODAY_DAY}</span>/{DAYS_IN_MONTH} IST
          </div>
        </div>

        {/* Main tab bar */}
        <div className="ml-4 flex items-center gap-0.5 bg-white/5 rounded-sm p-0.5 border border-border/30">
          {MAIN_TABS.map(tab => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                data-testid={`main-tab-${tab.id}`}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-1.5 px-3 py-1 text-[10px] font-bold rounded-sm transition-all ${activeTab === tab.id ? "bg-white/15 text-foreground" : "text-muted-foreground hover:text-foreground"}`}
              >
                <Icon size={10} />
                {tab.label}
              </button>
            );
          })}
        </div>

        {/* Gantt sub-view tabs (only on Gantt tab) */}
        {activeTab === "gantt" && (
          <div className="flex items-center gap-1 bg-white/[0.04] rounded-sm p-0.5 border border-border/20">
            {GANTT_VIEWS.map(v => (
              <button key={v} data-testid={`view-tab-${v.toLowerCase()}`} onClick={() => { setGanttView(v); setSelectedGap(null); setSelectedId(null); }}
                className={`px-3 py-1 text-[10px] font-bold rounded-sm transition-all ${ganttView === v ? "bg-white/15 text-foreground" : "text-muted-foreground hover:text-foreground"}`}>
                {v}
                {v === "Gaps" && optimResult?.raw_gaps?.length > 0 && (
                  <span className="ml-1 text-[8px] text-red-400 font-black">{optimResult.raw_gaps.reduce((s, g) => s + g.idle_windows.length, 0)}</span>
                )}
              </button>
            ))}
          </div>
        )}

        <div className="ml-auto flex items-center gap-2">
          {activeTab === "gantt" && (
            <>
              <button
                data-testid="optimise-fleet-btn"
                onClick={() => handleOptimise(!!optimResult)}
                disabled={optimLoading}
                className="flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-bold rounded-sm border transition-all disabled:opacity-50"
                style={{ backgroundColor: "rgba(45,212,191,0.15)", borderColor: "rgba(45,212,191,0.5)", color: "#2dd4bf" }}
              >
                {optimLoading ? <RefreshCw size={11} className="animate-spin" /> : <Zap size={11} />}
                {optimLoading ? "Analysing..." : optimResult ? "Re-Optimise" : "Optimise Fleet (AI)"}
              </button>
              <button
                data-testid="new-pairing-btn"
                onClick={() => setShowCreate(true)}
                className="flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-bold rounded-sm bg-amber-500/15 border border-amber-500/40 text-amber-300 hover:bg-amber-500/25 transition-all"
              >
                <Plus size={11} /> New Pairing
              </button>
            </>
          )}
          <button data-testid="refresh-fleet-btn" onClick={loadData} title="Refresh" className="p-1.5 text-muted-foreground hover:text-foreground transition-colors border border-border/30 rounded-sm">
            <RefreshCw size={12} />
          </button>
        </div>
      </div>

      {/* ── Stats bar ──────────────────────────────────────────────────────── */}
      <div className="flex items-center gap-4 px-4 py-2 border-b border-border/30 bg-white/[0.02] flex-shrink-0">
        {[
          ["Aircraft", aircraft.length, "text-foreground"],
          ["Active", activeCount, "text-emerald-400"],
          ["AOG/MX", groundedCount, groundedCount > 0 ? "text-red-400" : "text-muted-foreground"],
          ["Rotations", displayRotations, "text-amber-400"],
          ["Avg Util", `${avgUtil}%`, avgUtil >= 70 ? "text-emerald-400" : "text-amber-400"],
          ["Avg Block", `${avgHpd}h/d`, "text-teal-400"],
        ].map(([label, val, cls]) => (
          <div key={label} className="flex items-baseline gap-1.5">
            <span className="text-[9px] uppercase tracking-widest text-muted-foreground">{label}</span>
            <span className={`text-sm font-mono font-black ${cls}`} data-testid={`stat-${label.toLowerCase().replace(/\s/g,"-")}`}>{val}</span>
          </div>
        ))}
        {optimisedExtras > 0 && (
          <span className="text-[9px] text-teal-400/70 font-mono">+{optimisedExtras} optimised</span>
        )}
        {scenarioExtraLegs > 0 && (
          <span className="text-[9px] text-indigo-400/70 font-mono">+{Math.floor(scenarioExtraLegs/2)} scenario</span>
        )}
        {optimResult && (
          <div className="ml-auto flex items-center gap-1.5 text-[10px]">
            <Zap size={10} className="text-teal-400" />
            <span className="text-teal-400 font-bold">AI Score: {optimResult.efficiency_score}/100</span>
          </div>
        )}
      </div>

      {/* ── Tab Content ────────────────────────────────────────────────────── */}
      {loading ? (
        <div className="flex-1 flex items-center justify-center">
          <div className="text-muted-foreground text-sm animate-pulse">Loading fleet schedule...</div>
        </div>
      ) : (
        <>
          {/* GANTT TAB */}
          {activeTab === "gantt" && (
            <div className="flex-1 min-h-0 flex overflow-hidden">
              <FleetBoard
                aircraft={displayAircraft}
                selectedTail={filterTail}
                onSelect={tail => { setFilterTail(tail || ""); setSelectedId(null); setSelectedGap(null); }}
                activeBase={activeBase}
                onBase={setActiveBase}
              />
              <GanttTimeline
                aircraft={displayAircraft}
                pairings={pairings}
                selectedId={selectedId}
                onSelect={p => { setSelectedId(p.id); setSelectedGap(null); }}
                view={ganttView}
                optimResult={optimResult}
                selectedGap={selectedGap}
                onSelectGap={gap => { setSelectedGap(gap); setSelectedId(null); }}
                filterTail={filterTail}
              />
              {showGapPanel && (
                <GapDetailPanel gap={selectedGap} optimResult={optimResult} onClose={() => setSelectedGap(null)} />
              )}
              {showDetailPanel && (
                <DetailPanel pairing={selectedPairing} onClose={() => setSelectedId(null)} />
              )}
              {showAIPanel && (
                <AIInsightsPanel result={optimResult} setView={setGanttView} onClose={() => setOptimResult(null)} />
              )}
            </div>
          )}

          {/* ROUTE MAP TAB */}
          {activeTab === "route-map" && (
            <div className="flex-1 min-h-0 flex flex-col overflow-hidden">
              {/* Scenario banner */}
              {loadedScenarioName && (
                <div className="flex items-center gap-3 px-4 py-1.5 bg-teal-500/10 border-b border-teal-500/25 flex-shrink-0">
                  <BookmarkCheck size={11} className="text-teal-400" />
                  <span className="text-[10px] text-teal-300">
                    Viewing scenario: <span className="font-bold">{loadedScenarioName}</span>
                  </span>
                  <button
                    onClick={handleClearScenario}
                    className="ml-auto flex items-center gap-1 text-[10px] text-muted-foreground hover:text-foreground transition-colors"
                    data-testid="clear-scenario-btn"
                  >
                    <X size={10} /> Back to Live
                  </button>
                </div>
              )}
              <div className="flex-1 min-h-0">
                <PairingRouteMap
                  pairings={mapPairings !== null ? mapPairings : pairings}
                  optimResult={mapOptimResult !== null ? mapOptimResult : optimResult}
                  aircraft={aircraft}
                  activeBase={activeBase}
                  fleetSettings={fleetSettings}
                  mapScenario={mapScenario}
                />
              </div>
            </div>
          )}

          {/* FLEET SETTINGS TAB */}
          {activeTab === "fleet-settings" && (
            <FleetSettingsTab
              settings={fleetSettings}
              onSave={handleSaveSettings}
            />
          )}

          {/* SCENARIOS TAB */}
          {activeTab === "scenarios" && (
            <ScenariosTab
              activeBase={activeBase}
              onLoad={handleLoadScenario}
              onSubmit={() => setActiveTab("rostering")}
            />
          )}

          {/* ROSTERING TAB */}
          {activeTab === "rostering" && (
            <div className="flex-1 min-h-0 flex flex-col overflow-hidden">
              <ScenarioRostering />
            </div>
          )}
        </>
      )}

      {/* ── Legend (Gantt only) ─────────────────────────────────────────────── */}
      {activeTab === "gantt" && !loading && (
        <div className="px-4 py-2 border-t border-border/30 bg-white/[0.01] flex items-center gap-4 flex-shrink-0 flex-wrap">
          <span className="text-[9px] font-bold uppercase tracking-widest text-muted-foreground">Legend</span>
          {[
            ["bg-amber-400/40 border border-amber-400/50", "Present Rotation"],
            ["bg-teal-400/40 border-2 border-teal-400/80", "AI Optimised"],
            ["bg-red-400/30 border border-red-400/50", "Idle Gap"],
            ["bg-white/10 border border-white/20", "Past"],
          ].map(([cls, label]) => (
            <div key={label} className="flex items-center gap-1.5">
              <div className={`w-5 h-3 rounded-sm ${cls}`} />
              <span className="text-[9px] text-muted-foreground">{label}</span>
            </div>
          ))}
          <div className="flex items-center gap-1.5">
            <div className="w-0.5 h-4 bg-amber-400/60 rounded" />
            <span className="text-[9px] text-muted-foreground">Today (Day {TODAY_DAY})</span>
          </div>
        </div>
      )}

      {showCreate && (
        <CreatePairingModal aircraft={aircraft} onClose={() => setShowCreate(false)} onCreate={handleCreate} />
      )}
    </div>
  );
}
